import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useBranch } from "@/contexts/BranchContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { fmtIDR, fmtShortIDR, fmtDateTime, firstOfMonthISO, todayISO } from "@/lib/format";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from "recharts";
import { Target } from "lucide-react";

const COLORS = [
  "hsl(173 58% 39%)",
  "hsl(271 76% 53%)",
  "hsl(24 95% 53%)",
  "hsl(197 71% 52%)",
  "hsl(142 71% 45%)",
  "hsl(340 75% 55%)",
  "hsl(43 89% 50%)",
];

export default function Initiatives() {
  const { branchId } = useBranch();
  const [dateFrom, setDateFrom] = useState(firstOfMonthISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const [report, setReport] = useState(null);
  const [selected, setSelected] = useState(null);
  const [patients, setPatients] = useState([]);

  const fetchReport = () => {
    api
      .get("/initiatives", {
        params: { branch_id: branchId, date_from: dateFrom, date_to: dateTo },
      })
      .then((r) => {
        setReport(r.data);
        if (!selected && r.data.items.length) setSelected(r.data.items[0].type);
      });
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, dateFrom, dateTo]);

  useEffect(() => {
    if (!selected) return;
    api
      .get(`/initiatives/${selected}/patients`, {
        params: { branch_id: branchId, date_from: dateFrom, date_to: dateTo },
      })
      .then((r) => setPatients(r.data));
  }, [selected, branchId, dateFrom, dateTo]);

  const items = report?.items || [];
  const selectedItem = items.find((i) => i.type === selected);

  return (
    <div className="space-y-6" data-testid="initiatives-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl tracking-tight">Report Metric Inisiatif</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            MTD, achievement %, dan analisa per inisiatif strategis.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="text-xs text-muted-foreground">Dari</label>
            <Input
              type="date"
              data-testid="initiatives-date-from"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-[160px]"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Sampai</label>
            <Input
              type="date"
              data-testid="initiatives-date-to"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-[160px]"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {items.map((it, idx) => (
          <button
            key={it.type}
            data-testid={`initiative-card-${it.type}`}
            onClick={() => setSelected(it.type)}
            className={`rounded-lg border p-4 text-left transition-all hover:-translate-y-0.5 hover:shadow-sm ${
              selected === it.type ? "border-foreground ring-1 ring-foreground" : "border-border"
            } bg-card`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {it.label}
              </div>
              <span
                className="grid h-7 w-7 place-items-center rounded-md"
                style={{ backgroundColor: COLORS[idx % COLORS.length] + "22" }}
              >
                <Target className="h-3.5 w-3.5" style={{ color: COLORS[idx % COLORS.length] }} />
              </span>
            </div>
            <div className="mt-3 font-heading text-2xl tracking-tight">
              {fmtShortIDR(it.revenue)}
            </div>
            <div className="text-xs text-muted-foreground">
              Target: {fmtShortIDR(it.target)} • {it.tx_count} tx • {it.patient_count} pasien
            </div>
            <div className="mt-3">
              <Progress value={Math.min(it.achievement_pct, 100)} className="h-2" />
              <div className="mt-1 text-xs font-medium">
                {it.achievement_pct}% achievement
              </div>
            </div>
          </button>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg">Perbandingan Revenue Inisiatif</CardTitle>
          <CardDescription>
            Periode {report?.date_from} sampai {report?.date_to}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={items} margin={{ left: 8, right: 8 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1_000_000).toFixed(0)}jt`} />
                <Tooltip formatter={(v) => fmtIDR(v)} />
                <Bar dataKey="revenue" radius={[6, 6, 0, 0]}>
                  {items.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg">
            Daftar Pasien — {selectedItem?.label || "—"}
          </CardTitle>
          <CardDescription>
            Klik kartu inisiatif di atas untuk melihat pasien yang bertransaksi.
          </CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Pasien</TableHead>
                <TableHead>No HP</TableHead>
                <TableHead>Tanggal</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {patients.slice(0, 200).map((p, i) => (
                <TableRow key={i} data-testid={`init-patient-${i}`}>
                  <TableCell className="text-sm font-medium">{p.patient_name}</TableCell>
                  <TableCell className="text-xs">{p.patient_phone}</TableCell>
                  <TableCell className="text-xs">{fmtDateTime(p.paid_at)}</TableCell>
                  <TableCell className="text-right text-sm">{fmtIDR(p.amount)}</TableCell>
                </TableRow>
              ))}
              {patients.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-sm text-muted-foreground py-8">
                    Tidak ada transaksi di inisiatif ini untuk periode terpilih.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
